import { IPublicClientApplication } from '@azure/msal-browser';
import { TokenManager, TOKEN_EXPIRY_BUFFER, isRedirectLocked, setLastRedirectTimestamp } from '../interface/TokenManager';
import { AZURE_LOGIN_SCOPES } from '../component/azure/AzureOauth2Environment';

export class AzureTokenManager implements TokenManager {
    constructor(private instance: IPublicClientApplication) {}

    async isTokenExpiring(): Promise<boolean> {
        const activeAccount = this.instance.getActiveAccount();
        if (!activeAccount) {
            return true;
        }
        
        // Check if we have token claims with expiration time
        const idTokenClaims = activeAccount.idTokenClaims;
        if (!idTokenClaims || !idTokenClaims.exp) {
            return true;
        }
        
        const expirationTime = idTokenClaims.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const timeUntilExpiration = expirationTime - currentTime;
        
        // Return true if token will expire in less than 5 minutes
        return timeUntilExpiration < TOKEN_EXPIRY_BUFFER;
    }

    async refreshToken(): Promise<string> {
        try {
            const activeAccount = this.instance.getActiveAccount();
            const response = await this.instance.acquireTokenSilent({
                scopes: AZURE_LOGIN_SCOPES,
                account: activeAccount ?? undefined,
            });
            
            // Get the updated account info after token refresh
            const updatedAccount = this.instance.getAccountByUsername(activeAccount?.username || '');
            if (updatedAccount) {
                this.instance.setActiveAccount(updatedAccount);
            }
            
            return response.idToken;
        } catch (error) {
            console.error('[AzureTokenManager] Token renewal failed:', error);
            throw error;
        }
    }

    async getAccessToken(): Promise<string | null> {
        try {
            const activeAccount = this.instance.getActiveAccount();
            if (!activeAccount) {
                return null;
            }
            
            const response = await this.instance.acquireTokenSilent({
                scopes: AZURE_LOGIN_SCOPES,
                account: activeAccount,
            });
            
            return response.idToken;
        } catch (error) {
            console.error('[AzureTokenManager] Failed to get access token:', error);
            return null;
        }
    }

    async loginRedirect(): Promise<void> {
        if (isRedirectLocked()) {
            console.error('Redirect locked. Please wait before trying again.');
            return;
        }

        setLastRedirectTimestamp();
        await this.instance.loginRedirect({
            scopes: AZURE_LOGIN_SCOPES,
            redirectUri: this.instance.getConfiguration().auth.redirectUri,
        });
    }

    isRedirectLocked(): boolean {
        return isRedirectLocked();
    }

    lockRedirect(): void {
        setLastRedirectTimestamp();
    }
}
